/**
 * As formas de um desenho de planta, como filhos de um `<svg>`.
 *
 * Um componente só, usado pelo editor e pela planta em modo leitura, porque
 * as duas precisam desenhar exatamente a mesma coisa: se divergirem, o
 * usuário desenha uma planta e recebe outra — e a impressa no relatório
 * seria uma terceira.
 *
 * Não emite o `<svg>`: quem o cria decide `viewBox`, tamanho e o que mais
 * vai junto (fundo, marcadores, alças de edição). Aqui só o conteúdo.
 *
 * TEXTO VAI POR `<text>`, nunca por HTML injetado. O rótulo é digitado pelo
 * usuário, e o React escapa o conteúdo de `<text>` como escaparia qualquer
 * nó — é o que impede um nome de cômodo de virar marcação dentro do SVG.
 */

import {
  caminhoDaPorta, tema as temaDaPlanta, type Forma, type TemaDaPlanta,
} from "@/lib/plantaDesenho";

/** Espessura das paredes, em unidades do documento. */
const PAREDE = 6;

export function FormasDaPlanta({
  formas, selecionada, tema,
}: {
  formas: Forma[];
  /** Só o editor passa — em leitura nada fica selecionado. */
  selecionada?: string | null;
  /** Tema do PAPEL. Ausente = claro. */
  tema?: TemaDaPlanta;
}) {
  const t = temaDaPlanta(tema);
  return (
    <>
      {formas.map((f) => {
        const viva = selecionada === f.id;
        switch (f.tipo) {
          case "comodo": {
            const cor = t.comodos[f.cor] ?? t.comodos.neutro;
            return (
              <g key={f.id}>
                <rect
                  x={f.x} y={f.y} width={f.w} height={f.h} rx={4}
                  fill={cor.preenche}
                  stroke={viva ? "#0ea5e9" : cor.traco}
                  strokeWidth={viva ? 4 : 2}
                />
                {f.rotulo && (
                  // Centralizado no cômodo e sem `pointer-events`: clicar no
                  // nome precisa selecionar o cômodo, não o texto.
                  <text
                    x={f.x + f.w / 2} y={f.y + f.h / 2}
                    textAnchor="middle" dominantBaseline="middle"
                    fontSize={18} fill={t.tinta} pointerEvents="none"
                  >
                    {f.rotulo}
                  </text>
                )}
              </g>
            );
          }
          case "parede":
            return (
              <line
                key={f.id}
                x1={f.x1} y1={f.y1} x2={f.x2} y2={f.y2}
                stroke={viva ? "#0ea5e9" : t.traco}
                strokeWidth={viva ? PAREDE + 3 : PAREDE}
                strokeLinecap="round"
              />
            );
          case "porta":
            return (
              <path
                key={f.id}
                d={caminhoDaPorta(f)}
                fill="none"
                stroke={viva ? "#0ea5e9" : t.traco}
                strokeWidth={viva ? 5 : 3}
                strokeLinecap="round"
                // Tracejado no arco é a convenção de planta pro sentido de
                // abertura; a folha sólida é a porta em si.
                strokeDasharray="none"
              />
            );
          case "rotulo":
            return (
              <text
                key={f.id}
                x={f.x} y={f.y}
                fontSize={20} fontWeight={600}
                fill={viva ? "#0ea5e9" : t.tinta}
              >
                {f.texto}
              </text>
            );
        }
      })}
    </>
  );
}
